import type { RescheduleConfirmationStep } from './build-reschedule-confirmation-steps';

import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';

interface RescheduleConfirmationModalProps {
  open: boolean;
  steps: RescheduleConfirmationStep[];
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const StepBadge = memo(({ stepNumber }: { stepNumber: number }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      bgcolor: 'grey.900',
      color: 'common.white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      typography: 'caption',
      fontWeight: 700,
    }}
  >
    {stepNumber}
  </Box>
));

StepBadge.displayName = 'StepBadge';

const ConfirmationStepRow = memo(({ step }: { step: RescheduleConfirmationStep }) => {
  const { t } = useTranslation('shuffle');

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 24 }}>
      <StepBadge stepNumber={step.stepNumber} />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {t(`confirmationModal.steps.${step.key}`, {
          person: step.person,
          date: step.date ?? '',
        })}
        <Typography component="span" variant="body2" color="text.secondary">
          {` #${step.referenceId}`}
        </Typography>
      </Typography>
    </Stack>
  );
});

ConfirmationStepRow.displayName = 'ConfirmationStepRow';

export const RescheduleConfirmationModal = memo(
  ({ open, steps, isSubmitting = false, onClose, onConfirm }: RescheduleConfirmationModalProps) => {
    const { t } = useTranslation('shuffle');

    return (
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 480,
            maxWidth: '100%',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="span">
            {t('confirmationModal.title')}
          </Typography>
          <IconButton
            aria-label={t('confirmationModal.close')}
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ width: 28, height: 28, mt: -0.25, mr: -0.5 }}
          >
            <Iconify icon="mingcute:close-line" width={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {steps.map((step) => (
              <ConfirmationStepRow key={step.stepNumber} step={step} />
            ))}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 2,
            gap: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSubmitting}>
            {t('confirmationModal.cancel')}
          </Button>
          <Button variant="contained" color="inherit" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? t('confirmationModal.confirming') : t('confirmationModal.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

RescheduleConfirmationModal.displayName = 'RescheduleConfirmationModal';
